import { createApi } from '@reduxjs/toolkit/query/react';
import { newBackendAxiosBaseQuery } from '../../utils/axiosBaseQuery';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';
import type { TenantCancellationRule } from '../account/accountApiTypes';

/**
 * Product categories — the unit a tenant configures its booking flow on
 * (ADR-0026 §2 and its 2026-09-23/24 amendments).
 *
 * CRUD lives on the new (ECS) backend at `/admin/product-categories`; see
 * `new-backend/admin/src/controllers/productCategoryController.ts`.
 *
 * URLs here carry NO `/admin` prefix: `REACT_APP_API_NEW_BACKEND_URL` already
 * ends in `/admin`, and writing it twice produces `/admin/admin/…` and a 404
 * whose body reads `Cannot GET /admin/admin/…`. That exact mistake hit five
 * slices across #2261 and #2266, the second of them as a production hotfix.
 */

/**
 * What a tenant configures for ONE category (ADR-0026 §B).
 *
 * Every field optional, and that is the contract rather than laziness: an
 * absent field means "keep the use case's value", which is how a tenant
 * changes one knob without restating the other three. Sending `null` clears
 * an override; sending `{}` removes the category's block entirely.
 */
export interface ProductCategoryConfig {
    charging?: 'free' | 'mixed' | 'chargeable';
    directBooking?: boolean;
    eventTypes?: string[];
    /** A further category: only ever `{ enabled: false }` — it may switch the
     *  use case's check-in flow OFF, not configure one of its own (see
     *  `categoryConfigInput.ts`). The STANDARD category switches the use
     *  case's own flow on or off (ADR-0026 §AF). */
    checkInCheckOut?: { enabled: boolean };
    /**
     * How a reservation of THIS category is settled — pre-pay or post-pay, and
     * through which channels. Absent inherits the use case's arrangement.
     *
     * The case it exists for: one `venue` use case carrying a sports pitch paid
     * by card at booking and a village hall invoiced afterwards.
     */
    payment?: { flow: string, channels: string[] };
    bookingWindow?: {
        leadMinutes?: number;
        horizonMonths?: number;
        followUpMinutes?: number;
        /** Apartments only — see `hasMinStay` in `bookingWindowRules.ts`. */
        minNights?: number;
    };
    /**
     * The note customers read on the venue booking screen (#2889). Venue
     * categories only, and no "inherits" state: there is no use-case note
     * behind it, so absent simply means no note is shown.
     */
    note?: { de?: string, en?: string };
    /**
     * #3035 (M1): does the customer's booking flow open on the date or on the
     * object? Space-template categories only; absent = date first, today's
     * flow. No "inherits": no use case carries one.
     */
    entrySequence?: 'dateFirst' | 'objectFirst';
    /** #3035 (M2): the head-count field's word. Absent = guests. */
    headCountLabel?: 'guests' | 'participants' | 'members';
    /** #3069: `first` = the customer signs in before the flow's first screen. Absent = optional. */
    identification?: 'optional' | 'first';
    /**
     * How the venue booking step asks for the event: the event-type dropdown
     * (`select`, also what absence means) or a free-text name the customer
     * types (`freeText`, e.g. a club's "Damen 2"). Venue categories only; no
     * use-case fallback.
     */
    eventTypeInput?: 'select' | 'freeText';
    /**
     * The venue rate-unit knobs (#1713). Venue categories only. These change
     * what a booking COSTS: every pricing path (charge, admin preview, series
     * mails, end-user preview) reads them through the mirrored venueRate
     * resolver. Absent `weekendMode` = the legacy weekend formula, unchanged —
     * switching is a deliberate per-tenant act (PO decision 3, 2026-09-28).
     */
    rateUnits?: {
        weekendMode?: 'package' | 'per_day';
        /** 'HH:mm' local. Package mode's Friday split (default 13:00). */
        weekendStart?: string;
        /** 'HH:mm' local. Anchors the two half-day windows (default 13:00). */
        halfDayCutoff?: string;
    };
    /**
     * M10 (#3042): the cancellation rule. A booking field: on the standard
     * category it is the use case's entry. On a further category absent =
     * inherit, `'none'` = no rule here (the AGB governs). In a SAVE body,
     * `null` clears it and an absent field keeps what is stored.
     */
    cancellation?: TenantCancellationRule | 'none' | null;
}

export interface ProductCategory {
    id: string;
    /** The stable slug configuration points at. Chosen once, never changed. */
    key: string;
    name: string;
    /** Which use case this category is grouped under (ADR-0026 §G). */
    useCase: string;
    langNames: Record<string, { title?: string }> | null;
    /** The use case's STANDARD product category (ADR-0026 §AE): never
     *  archived, and its booking fields are the use case's own entry. */
    isStandard: boolean;
    /** #3016: the manual position among the use case's categories; null =
     *  never placed (listed after the placed ones, by name). Absent from an
     *  older backend. */
    sortOrder?: number | null;
    archivedAt: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    /** Absent when this category overrides nothing — which is every category
     *  until someone configures one. */
    config?: ProductCategoryConfig;
}

export interface CreateProductCategoryArgs {
    key: string;
    name: string;
    useCase: string;
    langNames?: Record<string, { title?: string }>;
}

/** The key is deliberately absent — it cannot be changed after creation. */
export interface UpdateProductCategoryArgs {
    id: string;
    name: string;
    /** `null` drops the per-language names, so the typed name is what shows. */
    langNames?: Record<string, { title?: string }> | null;
}

export const productCategoryApiSlice = createApi({
    reducerPath: 'productCategoryApi',
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: newBackendAxiosBaseQuery(),
    tagTypes: ['ProductCategories'],
    endpoints: (builder) => ({
        /**
         * The tenant's categories.
         *
         * `includeArchived` is an ARGUMENT rather than a second endpoint so the
         * two callers cannot drift — and because they genuinely want different
         * answers: the Services list has to show what was archived (there is no
         * other way to find it, and its key stays reserved), while the product
         * form's picker must not offer a category nothing can be filed under.
         *
         * Distinct arguments mean distinct cache entries, so one screen cannot
         * serve the other's rows.
         */
        getProductCategories: builder.query<
            ProductCategory[],
            { includeArchived?: boolean } | void
        >({
            query: (args) => ({
                url: '/product-categories',
                method: 'GET',
                ...(args && args.includeArchived
                    ? { params: { includeArchived: true } }
                    : {}),
            }),
            providesTags: ['ProductCategories'],
        }),
        createProductCategory: builder.mutation<ProductCategory, CreateProductCategoryArgs>({
            query: (data) => ({ url: '/product-categories', method: 'POST', data }),
            invalidatesTags: ['ProductCategories'],
        }),
        updateProductCategory: builder.mutation<ProductCategory, UpdateProductCategoryArgs>({
            query: ({ id, ...data }) => ({
                url: `/product-categories/${encodeURIComponent(id)}`,
                method: 'PUT',
                data,
            }),
            invalidatesTags: ['ProductCategories'],
        }),
        // Archive, never delete — an old booking's product still points here.
        /**
         * Replace one category's configuration block.
         *
         * A PUT, not a PATCH: the body IS the category's whole override set,
         * so a field the form leaves out is genuinely removed. That is what
         * makes "stop overriding this" expressible at all — with a merging
         * PATCH an override could be set but never taken back.
         */
        updateProductCategoryConfig: builder.mutation<
            { key: string, useCase: string, config: ProductCategoryConfig },
            { id: string, config: ProductCategoryConfig }
        >({
            query: ({ id, config }) => ({
                url: `/product-categories/${encodeURIComponent(id)}/config`,
                method: 'PUT',
                data: config,
            }),
            invalidatesTags: ['ProductCategories'],
        }),
        /**
         * Archive — and the products go with it (#2929). The response says how
         * many, because "archived" alone understates what just happened.
         */
        archiveProductCategory: builder.mutation<
            { archived: boolean, productsArchived: number },
            string
        >({
            query: (id) => ({
                url: `/product-categories/${encodeURIComponent(id)}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['ProductCategories'],
        }),
        /**
         * The master-only HARD delete (#2636) — `DELETE /:id` above archives,
         * so this one has its own path. The row and its configuration blocks
         * go; refused while any product is filed under the category.
         */
        deleteProductCategory: builder.mutation<
            { deleted: boolean, key: string, configRemoved: boolean },
            string
        >({
            query: (id) => ({
                url: `/product-categories/${encodeURIComponent(id)}/permanent`,
                method: 'DELETE',
            }),
            invalidatesTags: ['ProductCategories'],
        }),
        /** The inverse of the archive above — see the endpoint's own note. */
        restoreProductCategory: builder.mutation<ProductCategory, string>({
            query: (id) => ({
                url: `/product-categories/${encodeURIComponent(id)}/restore`,
                method: 'POST',
            }),
            invalidatesTags: ['ProductCategories'],
        }),
        /** #3016: the order of one use case's home tiles — every live further
         *  category of it, in the new order (the standard one stays first). */
        reorderProductCategories: builder.mutation<void, { useCase: string, ids: string[] }>({
            query: (data) => ({ url: '/product-categories/order', method: 'PUT', data }),
            invalidatesTags: ['ProductCategories'],
        }),
    }),
});

export const {
    useGetProductCategoriesQuery,
    useCreateProductCategoryMutation,
    useUpdateProductCategoryMutation,
    useUpdateProductCategoryConfigMutation,
    useArchiveProductCategoryMutation,
    useDeleteProductCategoryMutation,
    useRestoreProductCategoryMutation,
    useReorderProductCategoriesMutation,
} = productCategoryApiSlice;
